import type { ReactNode } from 'react';
import { cn } from './cn';

type Opt = { label: ReactNode; value: string | number; icon?: ReactNode; disabled?: boolean };

/** Ant Design `Segmented` replacement — a pill toggle group. */
export function Segmented({
  options, value, onChange, block, size = 'middle', disabled, className,
}: {
  options: (string | number | Opt)[];
  value?: string | number;
  onChange?: (v: string | number) => void;
  block?: boolean;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
  className?: string;
}) {
  const opts: Opt[] = options.map((o) => (typeof o === 'object' ? o : { label: String(o), value: o }));
  // On the canonical control scale (sm 32 / md 36 / lg 44) so a Segmented lines up with the Input/
  // Select/Button beside it in a toolbar row.
  const h = size === 'small' ? 'h-8 text-sm' : size === 'large' ? 'h-11 text-[15px]' : 'h-9 text-sm';
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-line bg-surface-2 p-1',
        // A 5-option toggle ("Today · This week · This month · This year · Custom") is ~400px wide and
        // pushed the whole page sideways on a 360px phone. Cap it to the container and let the strip
        // scroll instead — with the scrollbar hidden, since a bar inside a 36px pill reads as damage.
        // Touch swipe, trackpad and shift+wheel all still reach the far segments.
        'max-w-full overflow-x-auto scrollbar-none',
        block && 'flex w-full',
        className,
      )}
      role="tablist"
    >
      {opts.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={disabled || o.disabled}
            onClick={() => onChange?.(o.value)}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4 font-medium transition-colors [&>svg]:size-4', h,
              // In `block` mode the segments share the row (flex-1); otherwise they keep their natural
              // width and the track scrolls, rather than squashing labels to an unreadable sliver.
              block ? 'flex-1' : 'shrink-0',
              // Clean pill toggle: only the selected segment is filled — the brand green (like the primary
              // button) with white text; unselected segments stay fully transparent and only tint their
              // ink brand-green on hover, so nothing looks heavy and the active tab is unmistakable.
              active
                ? 'bg-brand-600 text-white font-semibold shadow-sm'
                : 'text-muted hover:text-brand-700 dark:hover:text-brand-300',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
            )}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
